import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { atlasFetch } from "@/lib/atlas-api";
import { Card, Skeleton, PrimaryButton, GhostButton } from "@/components/portal/PortalPrimitives";

const METRICS = [
  { value: "unique_users", label: "Unique end-users" },
  { value: "calls", label: "API calls" },
  { value: "bill_usd", label: "Estimated bill (USD)" },
];

function fmtVal(metric, v) {
  if (metric === "bill_usd") return `$${Number(v).toLocaleString()}`;
  return Number(v).toLocaleString();
}

export default function PortalAlerts() {
  const [cfg, setCfg] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const c = await atlasFetch("/api/v1/usage/alerts");
      setCfg(c);
      setThresholds(c.thresholds || []);
      setEmail(c.notify_email || "");
    } catch (e) {
      toast.error(e.message);
      setCfg({ thresholds: [], notify_email: "", current: { unique_users: 0, calls: 0, bill_usd: 0 } });
    }
  };

  useEffect(() => { load(); }, []);

  const addRow = () =>
    setThresholds((t) => [...t, { metric: "unique_users", value: 100 }]);

  const removeRow = (i) => setThresholds((t) => t.filter((_, idx) => idx !== i));

  const updateRow = (i, patch) =>
    setThresholds((t) => t.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const save = async () => {
    setSaving(true);
    try {
      await atlasFetch("/api/v1/usage/alerts", {
        method: "PUT",
        body: JSON.stringify({
          thresholds: thresholds.map((t) => ({ metric: t.metric, value: Number(t.value) })),
          notify_email: email,
        }),
      });
      toast.success("Alerts saved");
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Remove all usage alerts?")) return;
    try {
      await atlasFetch("/api/v1/usage/alerts", { method: "DELETE" });
      toast.success("Alerts cleared");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Usage alerts</p>
        <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">Get notified before the bill grows</h1>
      </div>

      {!cfg ? (
        <Card><Skeleton className="h-4 w-3/4" /><Skeleton className="mt-2 h-4 w-1/2" /></Card>
      ) : (
        <>
          <Card testId="alerts-current">
            <p className="text-[12px] uppercase tracking-[0.16em] text-slate-500">Current month</p>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-[14px]">
              <div className="rounded-[10px] bg-[#F5F1EB] px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.14em] text-[#1A4232]/70">Unique users</p>
                <p className="font-display text-[22px] text-[#1A4232] mt-1">{cfg.current.unique_users?.toLocaleString() || 0}</p>
              </div>
              <div className="rounded-[10px] bg-[#F5F1EB] px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.14em] text-[#1A4232]/70">API calls</p>
                <p className="font-display text-[22px] text-[#1A4232] mt-1">{cfg.current.calls?.toLocaleString() || 0}</p>
              </div>
              <div className="rounded-[10px] bg-[#1A4232] text-[#F5F1EB] px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.14em] opacity-70">Est. bill</p>
                <p className="font-display text-[22px] mt-1">${cfg.current.bill_usd?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-baseline justify-between">
              <p className="font-display text-[16px] text-[#0A1128]">Thresholds</p>
              <button onClick={addRow} data-testid="alerts-add" className="text-[12.5px] text-[#1A4232] inline-flex items-center gap-1 hover:underline">
                <Plus className="h-3.5 w-3.5" /> Add threshold
              </button>
            </div>

            {thresholds.length === 0 ? (
              <p className="mt-3 text-[13px] text-slate-500">No thresholds yet. Add one to start getting email alerts.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {thresholds.map((row, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`alerts-row-${i}`}>
                    <select
                      value={row.metric}
                      onChange={(e) => updateRow(i, { metric: e.target.value })}
                      data-testid={`alerts-row-${i}-metric`}
                      className="rounded-[10px] border border-slate-300 px-3 py-2 text-[13px] bg-white"
                    >
                      {METRICS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    <span className="text-[13px] text-slate-500">reaches</span>
                    <input
                      type="number"
                      min="0"
                      value={row.value}
                      onChange={(e) => updateRow(i, { value: e.target.value })}
                      data-testid={`alerts-row-${i}-value`}
                      className="w-32 rounded-[10px] border border-slate-300 px-3 py-2 text-[13px] tabular-nums"
                    />
                    {row.notified_months?.length ? (
                      <span className="text-[11px] text-slate-500">
                        already fired: {row.notified_months.join(", ")}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">≈ {fmtVal(row.metric, row.value)}</span>
                    )}
                    <button
                      onClick={() => removeRow(i)}
                      data-testid={`alerts-row-${i}-remove`}
                      className="text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 text-[12.5px] ml-auto"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="text-[12px] font-medium text-slate-700">Notify email</label>
                <input
                  type="email"
                  value={email}
                  data-testid="alerts-email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="alerts@yourcompany.com"
                  className="mt-1 w-full rounded-[10px] border border-slate-300 px-3 py-2 text-[14px] outline-none focus:border-[#1A4232]"
                />
              </div>
              <div className="flex gap-2">
                <GhostButton onClick={clearAll}>Clear all</GhostButton>
                <PrimaryButton testId="alerts-save" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </PrimaryButton>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
